import { TenantSyncStateEnum } from '@reach/shared-types/tenant-filter';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useTenantApi } from '../../../hooks/useTenantApi';
import { engageLibraryKeys } from './engage-library-query-keys';
import { EngageLibraryJobStatusEnum, EngageLibraryResponse } from './engage-library-types';
import { getTenantEngageLibrary, getTenantEngageLibraryJobStatus } from './tenant-engage-library-api';

const SYNCING_REFRESH_MS = 3000;

const hasSyncingItem = (library: EngageLibraryResponse | undefined): boolean =>
  [...(library?.automations ?? []), ...(library?.segments ?? [])].some(
    item => item.tenantSyncState === TenantSyncStateEnum.SYNCING,
  );

export function useTenantEngageLibrary() {
  const { tenantId } = useTenantApi();
  return useQuery({
    queryKey: engageLibraryKeys.library(tenantId),
    queryFn: () => getTenantEngageLibrary(tenantId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: query => (hasSyncingItem(query.state.data) ? SYNCING_REFRESH_MS : false),
  });
}

const TERMINAL_JOB_STATUSES = [EngageLibraryJobStatusEnum.COMPLETED, EngageLibraryJobStatusEnum.FAILED];
export const JOB_POLL_TIMEOUT_MS = 5 * 60 * 1000;

export class JobPollTimeoutError extends Error {
  constructor() {
    super('Timed out waiting for the Engage library sync to finish');
  }
}

export interface PolledJob {
  jobId: string;
  startedAt: number;
}

export interface EngageLibraryJobsState {
  /** Every job is finished, or its status could not be confirmed. */
  isSettled: boolean;
  hasFailedJob: boolean;
  hasUnverifiedJob: boolean;
}

const isTerminalJobStatus = (status: EngageLibraryJobStatusEnum | undefined): boolean =>
  status !== undefined && TERMINAL_JOB_STATUSES.includes(status);

/** Polls each job a save queued until all of them finish, for the tenant Engage tab. */
export function useTenantEngageLibraryJobs(jobs: PolledJob[]): EngageLibraryJobsState {
  const { tenantId } = useTenantApi();
  return useQueries({
    queries: jobs.map(job => ({
      queryKey: engageLibraryKeys.job(tenantId, job.jobId),
      queryFn: async () => {
        const jobStatus = await getTenantEngageLibraryJobStatus(tenantId, job.jobId);
        const isTerminal = isTerminalJobStatus(jobStatus.status);
        const pollWindowElapsed = Date.now() - job.startedAt > JOB_POLL_TIMEOUT_MS;
        if (!isTerminal && pollWindowElapsed) throw new JobPollTimeoutError();
        return jobStatus;
      },
      staleTime: 0,
      retry: (failureCount: number, error: Error) =>
        !(error instanceof JobPollTimeoutError) && failureCount < 3,
      refetchInterval: (query: { state: { data?: { status: EngageLibraryJobStatusEnum } } }) =>
        isTerminalJobStatus(query.state.data?.status) ? false : 2000,
    })),
    combine: jobQueries => ({
      isSettled:
        jobQueries.length > 0 &&
        jobQueries.every(jobQuery => jobQuery.isError || isTerminalJobStatus(jobQuery.data?.status)),
      hasFailedJob: jobQueries.some(
        jobQuery => jobQuery.data?.status === EngageLibraryJobStatusEnum.FAILED,
      ),
      hasUnverifiedJob: jobQueries.some(jobQuery => jobQuery.isError),
    }),
  });
}
